import { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { getCollection, optimizeImage } from '../lib/cms';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const location = useLocation();

  // Scroll detection for transparent-to-solid transition
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route changes
  useEffect(() => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const handleDropdownEnter = (label) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(label);
  };

  const handleDropdownLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch dynamic navigation items from CMS (with dependable default fallback)
  useEffect(() => {
    let isMounted = true;
    async function loadNav() {
      const data = await getCollection('navigation_items');
      if (isMounted && data && data.length > 0) {
        // Sort items by order
        const sorted = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));
        setNavItems(sorted);
      }
    }
    loadNav();
    return () => {
      isMounted = false;
    };
  }, []);

  // Default structure if CMS is loading
  const defaultTopItems = [
    { label: 'Forside', path: '/' },
    {
      label: 'Konsulentydelser',
      path: '/konsulentydelser',
      children: [
        { label: 'Mekanisk konstruktion', path: '/ydelser/mekanisk-konstruktion', desc: 'Udvikling og maskinkonstruktion' },
        { label: 'Elkonstruktion', path: '/ydelser/elkonstruktion', desc: 'Styretavler og eldiagrammer' },
        { label: 'CAD design og FEM', path: '/ydelser/cad-fem', desc: '2D/3D Inventor, NX, Creo og FEM analyse' },
        { label: 'Montage og indkøring', path: '/ydelser/montage-indkoring', desc: 'Mekanisk og elektrisk indkøring' },
        { label: 'Dokumentation og CE mærkning', path: '/ydelser/dokumentation-ce', desc: 'PC Schematik og CE mærkning' },
      ],
    },
    {
      label: 'Automation',
      path: '/automation',
      children: [
        { label: 'Automatiske maskiner', path: '/automation/automatiske-maskiner', desc: 'Udvikling og levering af specialmaskiner' },
        { label: 'Komplette løsninger', path: '/komplette-losninger', desc: 'Samlede mekatronik løsninger' },
        { label: 'Produktionsudstyr', path: '/produktionsudstyr', desc: 'Specialudstyr og produktionshjælpemidler' },
        { label: 'Værktøj', path: '/vaerktoj', desc: 'Specialværktøj og fiksturløsninger' },
        { label: 'Robot programmering', path: '/automation/robot-programmering', desc: 'KUKA, Motoman og UR robotter' },
        { label: 'PLC og SCADA', path: '/automation/plc-scada', desc: 'Siemens, Mitsubishi, Rockwell, WinCC' },
        { label: 'Problemløsninger', path: '/problemlosninger', desc: 'Tekniske udfordringer og optimering' },
      ],
    },
    { label: 'Projekter', path: '/projekter' },
    { label: 'Om JARCon', path: '/om-mig' },
    { label: 'Cykelservice', path: '/cykelservice' },
    { label: 'Kontakt', path: '/kontakt' },
  ];

  // Group CMS items if available
  const menuTree = (() => {
    if (!navItems.length) return defaultTopItems;
    const topLevel = navItems.filter((i) => !i.parent && i.path !== '/hifi' && !i.label.toLowerCase().includes('hifi'));
    return topLevel.map((parent) => {
      const children = navItems.filter((child) => child.parent === parent.label && child.path !== '/hifi');
      return {
        label: parent.label,
        path: parent.path,
        children: children.length
          ? children.map((c) => ({ label: c.label, path: c.path, desc: c.description }))
          : undefined,
      };
    });
  })();

  const isChildActive = (children) => {
    if (!children) return false;
    return children.some((c) => location.pathname === c.path);
  };

  const logoUrl = optimizeImage('https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-bc37f76e4fec45df81ec63fedfb4e897-mv2.png', 600);

  return (
    <header
      className={`sticky top-0 z-40 w-full transition-colors duration-200 ${
        isScrolled
          ? 'bg-[#0b111a]/98 backdrop-blur-md border-b border-slate-800/80 shadow-lg shadow-black/20'
          : 'bg-[#0b111a]/85 backdrop-blur-md border-b border-white/10'
      }`}
    >
      {/* Top industrial metadata bar */}
      <div
        className={`hidden min-[1200px]:block transition-colors duration-300 border-b text-xs text-slate-400 h-8 px-4 sm:px-6 lg:px-8 xl:px-10 ${
          isScrolled ? 'bg-[#070b10]/95 border-slate-900' : 'bg-slate-950/80 border-white/10'
        }`}
      >
        <div className="max-w-[1600px] mx-auto h-full flex justify-between items-center font-open-sans">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-slate-300">JARCon ApS</span>
              <span className="text-slate-600">|</span>
              <span>CVR: 39150255</span>
            </span>
            <span className="text-slate-500">Mekatronik • Konstruktion • Automation</span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="tel:+4522884536"
              className="flex items-center gap-2 hover:text-cyan-400 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-cyan-500 text-xs" aria-hidden="true" />
              <span className="whitespace-nowrap">+45 22 88 45 36</span>
            </a>
            <a
              href="mailto:jens@jarcon.dk"
              className="flex items-center gap-2 hover:text-cyan-400 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-cyan-500 text-xs" aria-hidden="true" />
              <span className="whitespace-nowrap">jens@jarcon.dk</span>
            </a>
            <span className="text-slate-500 whitespace-nowrap">
              <i className="fa-solid fa-location-dot text-cyan-500 mr-1 text-xs" aria-hidden="true" />
              6470 Sydals
            </span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between gap-4 lg:gap-6 h-20" ref={dropdownRef}>
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center py-2 group focus:outline-none focus:ring-2 focus:ring-cyan-500 rounded shrink-0"
            aria-label="JARCon ApS Forside"
          >
            <img
              src={logoUrl}
              alt="JARCon ApS"
              className="h-10 lg:h-11 xl:h-12 w-auto object-contain filter brightness-105 contrast-125 transition-transform duration-200 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation - Strictly displayed from 1200px and up */}
          <nav className="hidden min-[1200px]:flex items-center space-x-0.5 xl:space-x-1 shrink-0" aria-label="Hovednavigation">
            {menuTree.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isAnyChildActive = isChildActive(item.children);

              if (!hasChildren) {
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `px-2 xl:px-3 py-2 rounded-md text-xs xl:text-sm font-medium transition-colors whitespace-nowrap ${
                        isActive
                          ? 'text-cyan-400 bg-cyan-950/40 border border-cyan-500/30'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              }

              const isOpen = activeDropdown === item.label;

              return (
                <div
                  key={item.label}
                  className="relative group py-2"
                  onMouseEnter={() => handleDropdownEnter(item.label)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <NavLink
                    to={item.path}
                    onClick={() => setActiveDropdown(null)}
                    className={({ isActive }) =>
                      `flex items-center gap-1 xl:gap-1.5 px-2 xl:px-3 py-2 rounded-md text-xs xl:text-sm font-medium transition-colors whitespace-nowrap ${
                        isActive || isAnyChildActive
                          ? 'text-cyan-400 bg-cyan-950/40 border border-cyan-500/30'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
                      }`
                    }
                  >
                    <span>{item.label}</span>
                    <i
                      className={`fa-solid fa-chevron-down text-[9px] xl:text-[10px] transition-transform duration-200 ${
                        isOpen ? 'rotate-180 text-cyan-400' : 'text-slate-400'
                      }`}
                      aria-hidden="true"
                    />
                  </NavLink>

                  {/* Dropdown Menu - with top-full and an invisible safe hover bridge (pt-2) */}
                  {isOpen && (
                    <div
                      className="absolute left-0 top-full pt-1.5 w-72 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                      role="menu"
                      onMouseEnter={() => handleDropdownEnter(item.label)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <div className="rounded-lg bg-[#0e1622] border border-slate-700/80 shadow-2xl py-1.5 backdrop-blur-md">
                        {item.children.map((child) => {
                          const isChildCurr = location.pathname === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              role="menuitem"
                              onClick={() => setActiveDropdown(null)}
                              className={`block px-4 py-2 text-sm transition-colors ${
                                isChildCurr
                                  ? 'bg-cyan-950/40 text-cyan-400 font-medium border-l-2 border-cyan-400'
                                  : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                              }`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Desktop Right CTA */}
          <div className="hidden min-[1200px]:flex items-center gap-2 xl:gap-2.5 shrink-0">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-1.5 px-2.5 xl:px-3.5 py-2 rounded-md bg-cyan-500 hover:bg-cyan-600 text-white font-semibold text-xs xl:text-sm transition-all shadow-lg shadow-cyan-950/50 hover:shadow-cyan-500/20 active:scale-[0.98] whitespace-nowrap"
            >
              <span>Kontakt os</span>
              <i className="fa-solid fa-arrow-right text-[10px] xl:text-xs" aria-hidden="true" />
            </Link>

            <a
              href="https://d6e713fb-6411-4075-ab6a-5936b7a50d5a.sites.auxodev.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 xl:px-3 py-2 rounded-md bg-[#18181C]/90 hover:bg-[#222126] border border-[#C87D46]/60 hover:border-[#C87D46] text-[#C87D46] hover:text-[#D98E58] font-semibold text-xs xl:text-sm transition-all shadow-sm hover:shadow-[#C87D46]/20 active:scale-[0.98] whitespace-nowrap group"
              title="Besøg JARC Audio – HiFi & Lyd"
            >
              <i className="fa-solid fa-volume-high text-[11px] xl:text-xs text-[#C87D46] group-hover:scale-110 transition-transform" aria-hidden="true" />
              <span>JARC Audio</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-[9px] xl:text-[10px] text-[#C87D46]/70 group-hover:text-[#D98E58]" aria-hidden="true" />
            </a>
          </div>

          {/* Mobile hamburger button - Strictly visible under 1200px */}
          <div className="flex items-center min-[1200px]:hidden gap-3">
            <a
              href="https://d6e713fb-6411-4075-ab6a-5936b7a50d5a.sites.auxodev.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-2 text-xs font-semibold rounded-md bg-[#18181C] border border-[#C87D46]/60 text-[#C87D46] hover:text-[#D98E58] transition-colors whitespace-nowrap"
              title="Besøg JARC Audio – HiFi & Lyd"
            >
              <i className="fa-solid fa-volume-high text-xs" aria-hidden="true" />
              <span>JARC Audio</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-[9px] opacity-75" aria-hidden="true" />
            </a>
            <a
              href="tel:+4522884536"
              className="p-2.5 text-cyan-400 hover:text-white bg-slate-900 border border-slate-800 rounded-md whitespace-nowrap text-xs flex items-center gap-1.5"
              aria-label="Ring til +45 22 88 45 36"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span className="hidden sm:inline font-open-sans">+45 22 88 45 36</span>
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-lg bg-slate-900 border border-slate-700/60 text-slate-300 hover:text-white hover:border-cyan-500 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
            >
              {mobileMenuOpen ? (
                <i className="fa-solid fa-xmark text-xl w-6 h-6 flex items-center justify-center" aria-hidden="true" />
              ) : (
                <i className="fa-solid fa-bars text-xl w-6 h-6 flex items-center justify-center" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Navigation (visible when mobileMenuOpen is true on screens < 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-800 bg-[#0c121b] px-4 pt-3 pb-8 max-h-[85vh] overflow-y-auto">
          <div className="space-y-1">
            {menuTree.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isCurrent = location.pathname === item.path;
              const isAnyChildActive = isChildActive(item.children);

              if (!hasChildren) {
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                        isActive
                          ? 'text-cyan-400 bg-cyan-950/40 border-l-4 border-cyan-400'
                          : 'text-slate-200 hover:bg-slate-800/80 hover:text-white'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              }

              return (
                <div key={item.label} className="border-b border-slate-800/60 pb-2 mb-2">
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-4 py-2.5 rounded-md text-base font-semibold transition-colors ${
                        isActive || isAnyChildActive
                          ? 'text-cyan-400 bg-cyan-950/30'
                          : 'text-white hover:text-cyan-400'
                      }`
                    }
                  >
                    <span>{item.label}</span>
                    <i className="fa-solid fa-chevron-right text-xs text-slate-500" aria-hidden="true" />
                  </NavLink>
                  <div className="pl-4 pr-2 space-y-1 mt-1">
                    {item.children.map((child) => {
                      const isChildCurr = location.pathname === child.path;
                      return (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                            isChildCurr
                              ? 'text-cyan-400 bg-cyan-950/40 font-medium border-l-2 border-cyan-400'
                              : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                          }`}
                        >
                          {child.label}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-6 border-t border-slate-800 space-y-3">
            <Link
              to="/kontakt"
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-sm shadow-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span>Kontakt os i dag</span>
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </Link>

            <a
              href="https://d6e713fb-6411-4075-ab6a-5936b7a50d5a.sites.auxodev.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-[#18181C] hover:bg-[#222126] border border-[#C87D46]/60 text-[#C87D46] hover:text-[#D98E58] font-semibold text-sm transition-colors shadow-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="fa-solid fa-volume-high text-xs" aria-hidden="true" />
              <span>JARC Audio (HiFi &amp; Lyd)</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-75" aria-hidden="true" />
            </a>

            <div className="text-xs font-open-sans text-slate-400 space-y-2 pt-2 px-2">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-phone text-cyan-400 w-4" aria-hidden="true" />
                <a href="tel:+4522884536" className="text-slate-200 hover:text-cyan-400 whitespace-nowrap">
                  +45 22 88 45 36
                </a>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-cyan-400 w-4" aria-hidden="true" />
                <a href="mailto:jens@jarcon.dk" className="text-slate-200 hover:text-cyan-400 whitespace-nowrap">
                  jens@jarcon.dk
                </a>
              </div>
              <div className="flex items-start gap-2">
                <i className="fa-solid fa-location-dot text-cyan-400 w-4 mt-0.5" aria-hidden="true" />
                <div>
                  <span className="block text-slate-200">Toftehøj 21, 6470 Sydals</span>
                  <span className="block text-slate-400 text-[11px]">Produktion: Huholt 1A, 6400 Sønderborg</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
